import { TransportDetails } from 'src/app/impact/models/transport-details';

export interface Transport {
  // Present at runtime for saved transports (returned by GET /trips/:id).
  // Optional because newly added transports have no id until saved.
  id?: string;
  title?: string;
  type: TransportMode;
  subtype?: CarSubtype | CruiseSubtype;
  distance?: number;
  duration?: number;
  distancePerDay?: number;
  daysWithoutTravel?: number;
  startAddress?: string;
  endAddress?: string;
  flightStops?: FlightStop[];
  flightNumber?: string;
  flightDate?: string;
  carrierCode?: string;
  roadStops?: RoadStop[];
  passengerPerVehicle?: number;
  isPrivate?: boolean;
  // currently unused in calculation — kept for future reintroduction
  occupancyRate?: number;
  numberOfVehicle?: number;
  // A ajouter
  return?: boolean;
  isMainTransport?: boolean;
  vehicle?: number;
  transportImpactDetail?: TransportDetails;
  scheduledDays?: number[];
  customEmissionFactor?: { name: string };
}

export interface FlightStop {
  airportCode: string;
  order: number;
  type: FlightStopType;
}

export enum FlightStopType {
  Departure = 'departure',
  Stopover = 'stopover',
  Arrival = 'arrival',
}
export interface RoadStop {
  address: string;
  order: number;
  type: RoadStopType;
}

export enum RoadStopType {
  Origin = 'origin',
  Waypoint = 'waypoint',
  Destination = 'destination',
}

export enum TransportMode {
  RegionalTrain = 'regionalTrain',
  LongDistanceTrain = 'longDistanceTrain',
  HighspeedTrain = 'highspeedTrain',
  NightTrain = 'nightTrain',
  Eurostar = 'eurostar',
  Thalys = 'thalys',
  Metro = 'metro',
  Tram = 'tram',
  ICE = 'ice',
  OBB = 'obb',
  DieselTrain = 'dieselTrain',
  Lyria = 'lyria',
  Renfe = 'renfe',
  CombustionCar = 'combustionCar',
  HybridCar = 'hybridCar',
  PlugInHybridCar = 'plugInHybridCar',
  ElectricCar = 'electricCar',
  Taxi = 'taxi',
  Moto = 'moto',
  TukTuk = 'tukTuk',
  ElectricTukTuk = 'electricTukTuk',
  MotoLess250 = 'motoLess250',
  MotoMore250 = 'motoMore250',
  PrivateElectricMotorcycle = 'privateElectricMotorcycle',
  PrivateElectricBike = 'privateElectricBike',
  PrivateElectricKickScootering = 'privateElectricKickScootering',
  SharedElectricMotorcycle = 'sharedElectricMotorcycle',
  SharedElectricBike = 'sharedElectricBike',
  SharedElectricKickScootering = 'sharedElectricKickScootering',
  CoachBus = 'coachBus',
  CityBus = 'cityBus',
  ElectricBus = 'electricBus',
  Minibus = 'minibus',
  Van = 'van',
  ConvertedVan = 'convertedVan',
  Motorhome = 'motorhome',
  FerryWithCar = 'ferryWithCar',
  FerryWithoutCar = 'ferryWithoutCar',
  FerryPassengerOnly = 'ferryPassengerOnly',
  Cruise = 'cruise',
  PlaneEco = 'planeEco',
  PlaneBusiness = 'planeBusiness',
  PlaneFirst = 'planeFirst',
  PlanePremium = 'planePremium',
  Jet = 'jet',
  CustomFactor = 'custom',
}

export enum TransportCategory {
  Plane = 'plane',
  Train = 'train',
  Car = 'car',
  TwoWheels = 'twoWheels',
  Bus = 'bus',
  Boat = 'boat',
  Cruise = 'cruise',
  CustomFactor = 'custom',
}

export const PLANE_TYPES = [
  TransportMode.PlaneEco,
  TransportMode.PlaneBusiness,
  TransportMode.PlaneFirst,
  TransportMode.PlanePremium,
  TransportMode.Jet,
];

export const TRAIN_TYPES = [
  TransportMode.RegionalTrain,
  TransportMode.LongDistanceTrain,
  TransportMode.HighspeedTrain,
  TransportMode.NightTrain,
  TransportMode.Eurostar,
  TransportMode.Thalys,
  TransportMode.Metro,
  TransportMode.Tram,
  TransportMode.ICE,
  TransportMode.OBB,
  TransportMode.DieselTrain,
  TransportMode.Lyria,
  TransportMode.Renfe,
];

export const CAR_TYPES = [
  TransportMode.CombustionCar,
  TransportMode.HybridCar,
  TransportMode.PlugInHybridCar,
  TransportMode.ElectricCar,
  TransportMode.Taxi,
];

/**
 * Cars whose factor is published per passenger.km rather than per vehicle.km.
 * Mirrors PER_PASSENGER_CAR_TYPES in the API (app/enums/transport_mode.ts).
 *
 * They have no small/medium/big size to pick, because the factor already averages
 * the fleet — which is why the subtype control is gated on the type and not on the
 * Car category.
 */
export const PER_PASSENGER_CAR_TYPES = [TransportMode.Taxi];

export const TWO_WHEELS_TYPES = [
  TransportMode.Moto,
  TransportMode.TukTuk,
  TransportMode.ElectricTukTuk,
  TransportMode.MotoLess250,
  TransportMode.MotoMore250,
  TransportMode.PrivateElectricMotorcycle,
  TransportMode.PrivateElectricBike,
  TransportMode.PrivateElectricKickScootering,
  TransportMode.SharedElectricMotorcycle,
  TransportMode.SharedElectricBike,
  TransportMode.SharedElectricKickScootering,
];

export const BUS_TYPES = [
  TransportMode.CoachBus,
  TransportMode.CityBus,
  TransportMode.ElectricBus,
  TransportMode.Minibus,
  TransportMode.Van,
  TransportMode.ConvertedVan,
  TransportMode.Motorhome,
];

export const BOAT_TYPES = [
  TransportMode.FerryWithCar,
  TransportMode.FerryWithoutCar,
  TransportMode.FerryPassengerOnly,
  // TransportMode.Cruise,
];

export const PRIVATE_VEHICLE = [TransportMode.CoachBus];

export const PASSENGER_PER_VEHICLE = [
  TransportMode.CombustionCar,
  TransportMode.HybridCar,
  TransportMode.PlugInHybridCar,
  TransportMode.ElectricCar,
];
export enum CarSubtype {
  Big = 'big',
  Medium = 'medium',
  Small = 'small',
}

export enum CruiseSubtype {
  Big = 'big',
  Medium = 'medium',
  Small = 'small',
  River = 'river',
}

export const TRANSPORT_SUBTYPES = [
  ...Object.values(CruiseSubtype),
  ...Object.values(CarSubtype),
];
